import {
  STORAGE_KEYS,
  BUDGET_FLOW_KEYS,
  AUTH_KEYS,
} from './constants/localStorage';

// Re-export para facilitar importação
export {
  STORAGE_KEYS,
  BUDGET_FLOW_KEYS,
  AUTH_KEYS,
} from './constants/localStorage';

/**
 * Wrapper centralizado para operações de localStorage
 * Fornece tipagem e tratamento de erros consistente
 */
export const storage = {
  /**
   * Obtém um valor do localStorage
   * @param key - Chave do localStorage
   * @returns Valor parseado ou null se não existir ou erro
   */
  get: <T>(key: string): T | null => {
    if (typeof window === 'undefined') return null;

    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      return null;
    }
  },

  /**
   * Salva um valor no localStorage
   * @param key - Chave do localStorage
   * @param value - Valor a ser salvo (será serializado como JSON)
   */
  set: <T>(key: string, value: T): void => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Erro ao salvar no localStorage
    }
  },

  /**
   * Remove uma chave do localStorage
   * @param key - Chave a ser removida
   */
  remove: (key: string): void => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(key);
  },

  /**
   * Remove múltiplas chaves do localStorage
   * @param keys - Array de chaves a serem removidas
   */
  clear: (keys: string[]): void => {
    if (typeof window === 'undefined') return;
    keys.forEach(key => localStorage.removeItem(key));
  },

  /**
   * Limpa todas as chaves relacionadas ao fluxo de orçamento
   */
  clearBudgetFlow: (): void => {
    storage.clear([...BUDGET_FLOW_KEYS]);
    
    // Limpar chaves dinâmicas que começam com pedidoNames_
    if (typeof window !== 'undefined') {
      const allKeys = Object.keys(localStorage);
      for (const key of allKeys) {
        if (key.startsWith('pedidoNames_')) {
          localStorage.removeItem(key);
        }
      }
    }
    
    // Limpar campos adicionais que não estão nas constantes
    storage.remove(STORAGE_KEYS.BEST_ESTABLISHMENT_ID);
    storage.remove(STORAGE_KEYS.REACT_USE_CART);
  },

  /**
   * Limpa todas as chaves de autenticação
   */
  clearAuth: (): void => {
    storage.clear([...AUTH_KEYS]);
  },

  /**
   * Limpa todo o localStorage
   */
  clearAll: (): void => {
    if (typeof window === 'undefined') return;
    localStorage.clear();
  },

  /**
   * Obtém um valor do sessionStorage
   * @param key - Chave do sessionStorage
   * @returns Valor parseado ou null se não existir ou erro
   */
  getSession: <T>(key: string): T | null => {
    if (typeof window === 'undefined') return null;

    try {
      const item = sessionStorage.getItem(key);
      if (item === null) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      return null;
    }
  },

  /**
   * Salva um valor no sessionStorage
   * @param key - Chave do sessionStorage
   * @param value - Valor a ser salvo (será serializado como JSON)
   */
  setSession: <T>(key: string, value: T): void => {
    if (typeof window === 'undefined') return;

    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Erro ao salvar no sessionStorage
    }
  },

  /**
   * Remove uma chave do sessionStorage
   * @param key - Chave a ser removida
   */
  removeSession: (key: string): void => {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(key);
  },
};

/** Mesmas chaves que o checkout limpa em "novo evento" ao sair da edição de duplicação */
const DUPLICACAO_EDICAO_FORM_KEYS_BASE: string[] = [
  STORAGE_KEYS.FINAL_INFO,
  STORAGE_KEYS.FINAL_DATETIME,
  STORAGE_KEYS.FINAL_NOTES,
  STORAGE_KEYS.CEP,
  STORAGE_KEYS.CEP_DATA,
  STORAGE_KEYS.ENDERECO_DATA,
  STORAGE_KEYS.DISTANCIA,
  STORAGE_KEYS.ADDITIONAL_SERVICES,
  STORAGE_KEYS.CONFIRMED_SUGGESTION,
  STORAGE_KEYS.SELECTED_PRODUCTS_MANUAL,
  STORAGE_KEYS.SELECTED_PRODUCTS_CAROLINO,
  STORAGE_KEYS.SELECTED_PRODUCTS_ESSENCIAL,
  STORAGE_KEYS.SELECTED_PRODUCTS_CLASSICO,
  STORAGE_KEYS.EVENT_TYPE,
  STORAGE_KEYS.PESSOAS,
  STORAGE_KEYS.MODO_EDICAO_DUPLICACAO,
  STORAGE_KEYS.EVENTO_EM_EDICAO,
  STORAGE_KEYS.DUPLICACAO_EVENTO_EDITANDO_ID,
];

/**
 * Remove snapshot do formulário ao cancelar edição de evento duplicado (alinha com handleNewEvent).
 * `withCarrinhoId` reproduz o botão "Adicionar novo evento" no checkout.
 */
export function clearDuplicacaoEdicaoFormLocalStorage(options?: {
  withCarrinhoId?: boolean;
}): void {
  const keys = [...DUPLICACAO_EDICAO_FORM_KEYS_BASE];
  if (options?.withCarrinhoId) {
    keys.push(STORAGE_KEYS.CARRINHO_ID);
  }
  storage.clear(keys);
}

/**
 * true se `evento_em_edicao` ainda existe em `duplicacao_eventos` (carrinho virtual).
 */
export function eventoEmEdicaoDuplicacaoAindaExisteNoCarrinho(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const emStr = localStorage.getItem(STORAGE_KEYS.EVENTO_EM_EDICAO);
    if (!emStr) return false;
    const em = JSON.parse(emStr) as {
      duplicacaoId?: unknown;
      carrinho_id?: unknown;
    };
    const listStr = localStorage.getItem(STORAGE_KEYS.DUPLICACAO_EVENTOS);
    if (!listStr) return false;
    const list = JSON.parse(listStr) as unknown;
    if (!Array.isArray(list) || list.length === 0) return false;
    const dupId = em.duplicacaoId;
    const localId = em.carrinho_id;
    return list.some((ev: { id?: unknown; localId?: unknown }) => {
      const byDup = dupId != null && ev?.id === dupId;
      const byLocal =
        localId != null &&
        Number(ev?.localId ?? -999) === Number(localId);
      return byDup || byLocal;
    });
  } catch {
    return false;
  }
}

/**
 * Funções de conveniência usando as constantes STORAGE_KEYS
 */
export const storageHelpers = {
  // Auth
  getAuthToken: () => storage.get<string>(STORAGE_KEYS.AUTH_TOKEN),
  setAuthToken: (token: string) => storage.set(STORAGE_KEYS.AUTH_TOKEN, token),
  getUserId: () => storage.get<number>(STORAGE_KEYS.USER_ID),
  setUserId: (id: number) => storage.set(STORAGE_KEYS.USER_ID, id),
  getUserEmail: () => storage.get<string>(STORAGE_KEYS.USER_EMAIL),
  setUserEmail: (email: string) => storage.set(STORAGE_KEYS.USER_EMAIL, email),

  // Cart & Orders
  getCarrinhoId: () => storage.get<number>(STORAGE_KEYS.CARRINHO_ID),
  setCarrinhoId: (id: number) => storage.set(STORAGE_KEYS.CARRINHO_ID, id),
  getPedidoId: () => storage.get<number>(STORAGE_KEYS.PEDIDO_ID),
  setPedidoId: (id: number) => storage.set(STORAGE_KEYS.PEDIDO_ID, id),

  // Event
  getCep: () => storage.get<string>(STORAGE_KEYS.CEP),
  setCep: (cep: string) => storage.set(STORAGE_KEYS.CEP, cep),
  getEventType: () => storage.get<string>(STORAGE_KEYS.EVENT_TYPE),
  setEventType: (type: string) => storage.set(STORAGE_KEYS.EVENT_TYPE, type),
  getPessoas: () => storage.get<number>(STORAGE_KEYS.PESSOAS),
  setPessoas: (pessoas: number) => storage.set(STORAGE_KEYS.PESSOAS, pessoas),
};
